import { faHeart, faLeaf, faUser, faUtensils } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { fetchSavedRecipes, saveRecipe, unsaveRecipe } from './api';
import { auth, db } from './firebase';


function RecipeCard({ recipe }) {
  const [user] = useAuthState(auth);
  const [savedRecipes, setSavedRecipes] = useState([]);
  const [isSaved, setIsSaved] = useState(false);

  const fetchUserDocumentId = async (userUID) => {
    if (!userUID) return null;
    try {
      const usersCollection = collection(db, 'users');
      const userQuery = query(usersCollection, where('uid', '==', userUID));
      const querySnapshot = await getDocs(userQuery);
      if (!querySnapshot.empty) return querySnapshot.docs[0].id;
      return null;
    } catch (err) {
      console.error('fetchUserDocumentId error', err);
      return null;
    }
  };

  const toggleSave = async () => {
    try {
      if (isSaved) {
        await unsaveRecipe({ recipe });
        setIsSaved(false);
      } else {
        await saveRecipe({ recipe });
        setIsSaved(true);
      }
    } catch (error) {
      console.error('Error toggling save:', error);
    }
  };

  useEffect(() => {
    let mounted = true;
    if (user) {
      fetchUserDocumentId(user.uid)
        .then((userDocumentId) => fetchSavedRecipes(userDocumentId))
        .then((saved) => {
          if (!mounted) return;
          setSavedRecipes(saved || []);
          setIsSaved((saved || []).some((s) => s.id === recipe?.id));
        })
        .catch((err) => {
          console.error('fetch saved recipes', err);
          if (mounted) {
            setSavedRecipes([]);
            setIsSaved(false);
          }
        });
    } else {
      setSavedRecipes([]);
      setIsSaved(false);
    }
    return () => { mounted = false; };
  }, [user, recipe]);

  const imageSrc = recipe?.imageURL || (process.env.PUBLIC_URL + '/images/banner-food.webp');

  return (
    <div className="w-full sm:w-full z-100 max-w-sm ">
      <div className="relative rounded-xl overflow-hidden h-96">
        {/* Full image background */}
        <img
          src={imageSrc}
          alt={recipe?.title || 'recipe image'}
          className="absolute inset-0 w-full h-full object-cover"
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = process.env.PUBLIC_URL + '/images/banner-food.webp';
          }}
        />

        {/* Smooth gradient overlay with increasing blur towards bottom */}
        <div className="absolute left-0 right-0 bottom-0 h-56">
          {/* Multi-layered gradient for smoother transition */}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent" />
          {/* Blur effect that increases towards bottom */}
          <div className="absolute inset-0" style={{
            backdropFilter: 'blur(0px)',
            WebkitBackdropFilter: 'blur(0px)',
            maskImage: 'linear-gradient(to bottom, transparent 0%, black 100%)',
            WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 100%)'
          }}>
            <div className="w-full h-full backdrop-blur-xl" />
          </div>
        </div>

        {/* Top badges */}
        {recipe?.vegan === true && (
          <div className="absolute top-3 left-3 z-20">
            <div className="bg-white/10 backdrop-blur-sm rounded-full p-0.5">
              <span className="bg-green-600 text-white rounded-full p-2 text-xs shadow-md block">
                <FontAwesomeIcon icon={faLeaf} />
              </span>
            </div>
          </div>
        )}

        {recipe?.highProtein === true && (
          <div className="absolute top-3 left-16 z-20">
            <div className="bg-white/10 backdrop-blur-sm rounded-full p-0.5">
              <span className="bg-yellow-500 text-white rounded-full px-3 py-1 text-xs font-semibold shadow-md block">
                high protine
              </span>
            </div>
          </div>
        )}

        {user && (
          <div className="absolute top-3 right-3 z-50">
            <div className="bg-white/10 backdrop-blur-sm rounded-full p-0.5">
              <button onClick={(e)=>{ e.stopPropagation(); toggleSave(); }} className={`p-2 rounded-full ${isSaved ? 'text-red-500' : 'text-white/90'}`} aria-label="save recipe">
                <FontAwesomeIcon icon={faHeart} />
              </button>
            </div>
          </div>
        )}

        {/* Content overlay at bottom */}
        <div className="absolute left-0 right-0 bottom-0 z-30 p-2">
          <div className="text-white">
            <h3 className="text-base md:text-lg font-extrabold leading-tight drop-shadow-md">{recipe?.title}</h3>
            <p className="text-xs md:text-sm text-white/85 mt-2 mb-3">{recipe?.description ? `${recipe.description.split(' ').slice(0, 14).join(' ')}...` : 'No description'}</p>

            <div className="flex items-center gap-3 text-xs md:text-sm text-white/90 mb-3">
              <span className="inline-flex items-center gap-1 bg-white/10 backdrop-blur-sm px-2 py-0.5 rounded-full text-white/90">
                <span className="text-sm font-medium">{recipe?.calories ?? '—'}</span>
                <span className="text-xs opacity-90">kcal</span>
              </span>

              

              <span className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-2 py-0.5 rounded-full text-white/90">
                <FontAwesomeIcon icon={faUser} />
                <span className="ml-1">{recipe?.portionSize ?? '—'}</span>
              </span>

                {recipe?.difficulty && (
                <span className="inline-flex items-center gap-0.5 bg-white/10 backdrop-blur-sm px-2 py-2 rounded-full text-white/90">
                  {[1, 2, 3].map((level) => {
                    const difficultyLevel = recipe.difficulty.toLowerCase() === 'easy' ? 1 : recipe.difficulty.toLowerCase() === 'medium' ? 2 : 3;
                    return (
                      <FontAwesomeIcon
                        key={level}
                        icon={faUtensils}
                        className={`text-xs ${level <= difficultyLevel ? 'text-yellow-400' : 'text-white/30'}`}
                      />
                    );
                  })}</span>
              )}
            </div>

          

            <div className="flex justify-center">
              <button
                onClick={(e) => { e.stopPropagation(); window.location = `/recipe/${recipe.id}`; }}
                className="w-full px-6 py-2 rounded-full bg-white/20 backdrop-blur-sm text-white font-semibold shadow-md  hover:bg-white/30 transition-colors"
              >
                See Recipe
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RecipeCard;